import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { money } from "../lib/format";
import { downloadCsv } from "../lib/quarters";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";

const SOURCE_LABELS = {
  financials: "Captive financials",
  performance: "Captive performance",
  payments: "Vendor payments",
};

export function DrillDialog({ drill, filters, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!drill) { setData(null); return; }
    setLoading(true);
    api.get("/financials/drill", { params: { ...filters, period: drill.period, source: drill.source } })
      .then(({ data: payload }) => setData(payload))
      .catch((error) => toast.error(formatApiError(error.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [drill, filters]);

  const columns = data?.columns || [];

  return (
    <Dialog open={Boolean(drill)} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="!rounded-none max-w-[min(1200px,94vw)] max-h-[86vh] overflow-hidden flex flex-col"
        style={{ borderColor: "var(--border)" }}
        data-testid="drill-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-2xl tracking-tight">
            {drill?.title} · {drill?.period}
          </DialogTitle>
          <DialogDescription>
            {SOURCE_LABELS[drill?.source] || "Feed rows"} behind this bar — {data?.row_count ?? "…"} row(s)
            {filters?.company ? ` for ${filters.company}` : " across all companies"}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto border" style={{ borderColor: "var(--border)" }}>
          <table className="w-full text-xs" data-testid="drill-table">
            <thead className="sticky top-0" style={{ background: "#F3F3F1" }}>
              <tr className="eyebrow text-left">
                {columns.map((column) => (
                  <th key={column.key} className={`px-3 py-2 whitespace-nowrap ${column.money ? "text-right" : ""}`}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((row, index) => (
                <tr key={index} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`drill-row-${index}`}>
                  {columns.map((column) => (
                    <td key={column.key} className={`px-3 py-2 whitespace-nowrap ${column.money ? "text-right" : ""}`}
                        style={{ color: column.money ? "var(--ink)" : "var(--muted)" }}>
                      {column.money ? money(row[column.key]) : (row[column.key] || "—")}
                    </td>
                  ))}
                </tr>
              ))}
              {data && !data.rows.length && (
                <tr><td colSpan={columns.length || 1} className="p-8 text-center" style={{ color: "var(--muted)" }} data-testid="drill-empty">
                  No feed rows behind this bar.
                </td></tr>
              )}
              {loading && !data && (
                <tr><td className="p-8 text-center" style={{ color: "var(--muted)" }} data-testid="drill-loading">Loading feed rows…</td></tr>
              )}
            </tbody>
            {data?.rows?.length > 0 && (
              <tfoot className="sticky bottom-0" style={{ background: "#F3F3F1" }}>
                <tr className="border-t" style={{ borderColor: "var(--border)" }}>
                  {columns.map((column, index) => (
                    <td key={column.key} className={`px-3 py-2 font-heading font-bold whitespace-nowrap ${column.money ? "text-right" : ""}`}
                        style={{ color: "var(--ink)" }}>
                      {column.money ? money(data.totals[column.key]) : (index === 0 ? "Total" : "")}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            className="btn-ghost text-xs"
            disabled={!data?.rows?.length}
            onClick={() => downloadCsv(`${drill.source}-${drill.period}.csv`, data.rows, columns)}
            data-testid="drill-csv"
          >
            Download CSV
          </button>
          <button className="btn-primary text-xs" onClick={onClose} data-testid="drill-close">Close</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
